import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Database, useCreateDatabase } from '@/entities/database';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, DialogActions, TextAreaField, TextField } from '@/shared/ui';

import styles from './DatabaseCatalog.module.css';

export const DATABASE_NAME_MAX = 100;
const DESCRIPTION_MAX = 5000;

interface Props {
  /** Workspaces the viewer may create a database in (`workspace.create_database`). */
  workspaces: readonly Workspace[];
  /** Preselected workspace (the one the dialog was opened from). */
  workspaceId?: string;
  /** File the new database in this section of that workspace in the same request (UD-STRUCT). */
  sectionId?: string | null | undefined;
  onCreated: (database: Database) => void;
  onClose: () => void;
}

/** "New database": name, optional description, the workspace and the confidential mode. */
export function CreateDatabaseDialog({
  workspaces,
  workspaceId,
  sectionId,
  onCreated,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const create = useCreateDatabase();
  const [target, setTarget] = useState(workspaceId ?? workspaces[0]?.id ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [touched, setTouched] = useState(false);
  const nameError = touched && name.trim() === '' ? t('validation.REQUIRED') : undefined;

  const submit = () => {
    setTouched(true);
    if (name.trim() === '' || target === '') return;
    create.mutate(
      {
        workspaceId: target,
        name: name.trim(),
        ...(description.trim() && { description: description.trim() }),
        is_confidential: confidential,
        // only for the workspace the dialog was opened from (a section belongs to one workspace)
        ...(sectionId && target === workspaceId && { section_id: sectionId }),
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Dialog title={t('databases.createTitle')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t('databases.name')}
          value={name}
          maxLength={DATABASE_NAME_MAX}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextAreaField
          label={t('databases.description')}
          value={description}
          maxLength={DESCRIPTION_MAX}
          rows={3}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        {workspaces.length > 1 && (
          <Combobox
            label={t('databases.workspace')}
            value={target}
            options={workspaces.map((w) => ({
              value: w.id,
              label: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
            }))}
            onChange={setTarget}
          />
        )}
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={confidential}
            onChange={(event) => {
              setConfidential(event.target.checked);
            }}
          />
          <span>
            <strong>{t('databases.confidential')}</strong>
            <span className={styles.hint}>{t('databases.confidentialHint')}</span>
          </span>
        </label>
        <FormError error={create.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t('databases.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
